import { computed, onScopeDispose, useFormat, usePlayerAccountClient, usePlaygameEventTrigger, usePlaygameState, useCoreConfig, useGameSessionClient, useUserInfo, useUserAuthClient, useRoute, useState, useGameManager, navigateTo, useIntervalFn, useDayjs, useTracking, useLocalStorage } from '#imports'
import type { UISelectItemOption } from '@core/types'
import { EVENT_TOGGLE_AUTO_SPIN, EVENT_ENABLE_AUTO_PLAY } from '@playgame/constants'
import type { Dayjs } from 'dayjs'

export type Interval = { duration: number, text: string }

export interface SessionDetails {
    spentTotal?: number
    wonTotal?: number
}

export interface TimeSpent {
    hours: number
    minutes: number
}

export interface OpenOptions {
    configurationView?: boolean
}

export interface PlayClock {
    login?: string
    startedAt?: number
}

// Time conversion constants
const MILLISECONDS_PER_SECOND = 1000
const SECONDS_PER_MINUTE = 60
const MILLISECONDS_PER_MINUTE = SECONDS_PER_MINUTE * MILLISECONDS_PER_SECOND
const MILLISECONDS_PER_HOUR = SECONDS_PER_MINUTE * MILLISECONDS_PER_MINUTE

// Timer constants
const TIMER_INTERVAL_MS = MILLISECONDS_PER_SECOND

const REALITY_CHECK_UNLIMITED = 'inf'

export const useRealityCheck = (options: { initTicker?: boolean } = {}) => {
    const dayjs = useDayjs()
    const { isStaff } = useUserInfo()
    const { asStaticTime } = useFormat()
    const { trackEvent } = useTracking()
    const { useGetRealityCheckSetting, useUpdateRealityCheckSetting } = usePlayerAccountClient()
    const { getSessionInfo } = useUserAuthClient()
    const { realityCheck } = useCoreConfig()
    const { closePlaygame } = useGameManager()
    const { gamesArray } = usePlaygameState()
    const route = useRoute()
    const { getRealityCheck } = useGameSessionClient()
    const { eventTrigger } = usePlaygameEventTrigger()
    const settingQuery = useGetRealityCheckSetting()
    const updateSettingMutation = useUpdateRealityCheckSetting()

    const defaultInterval = realityCheck.defaultInterval

    // Shared state (via useState)
    const configurationView = useState('reality-check:configurationView', () => false)
    const sessionDetails = useState<SessionDetails>('reality-check:sessionDetails', () => ({}))
    const timeSpent = useState<TimeSpent>('reality-check:timeSpent', () => ({ hours: 0, minutes: 0 }))
    const playStartedAt = useState<Dayjs | undefined>('reality-check:playStartedAt', () => undefined)
    // kept in localStorage so a reload does not restart the clock; only a new login does
    const playClock = useLocalStorage<PlayClock>('reality-check:play-clock', {})
    const disabled = useState('reality-check:disabled', () => false)
    const isProcessing = useState('reality-check:isProcessing', () => false)
    const savedIntervalFromApi = useState<number | typeof REALITY_CHECK_UNLIMITED | undefined>('reality-check:savedIntervalFromApi', () => undefined)
    const selectedInterval = useState<number | undefined>('reality-check:selectedInterval', () => undefined)
    const timer = useState<number>('reality-check:timer', () => 0)
    const isTimerActive = useState<boolean>('reality-check:active', () => false)

    const pauseTimer = () => {
        isTimerActive.value = false
    }

    const resumeTimer = () => {
        isTimerActive.value = true
    }

    // Timer interval function — guarded to prevent duplicate tickers if initTicker: true
    // is ever passed by more than one caller simultaneously.
    const isTickerOwned = useState<boolean>('reality-check:ticker-owned', () => false)

    if (import.meta.client && options.initTicker && !isTickerOwned.value) {
        isTickerOwned.value = true

        useIntervalFn(() => {
            if (!isTimerActive.value) return

            timer.value++
        }, TIMER_INTERVAL_MS)

        onScopeDispose(() => {
            isTickerOwned.value = false
        })
    }

    const sessionQuery = getSessionInfo({
        enabled: true,
        lazy: true
    })

    const currentInterval = computed<number | typeof REALITY_CHECK_UNLIMITED>(() => {
        if (savedIntervalFromApi.value === REALITY_CHECK_UNLIMITED) {
            return realityCheck.isDisableable ? REALITY_CHECK_UNLIMITED : Number(defaultInterval)
        }

        return savedIntervalFromApi.value ?? Number(defaultInterval)
    })

    const isRealityCheckDisabled = computed(() => currentInterval.value === REALITY_CHECK_UNLIMITED)

    const hasValidSelectedInterval = computed(() => {
        return isValidInterval(selectedInterval.value)
    })

    const intervals = computed(() => {
        let intervalsList = [...realityCheck.selectableIntervals]

        // if staff, then add some shorter intervals to make testing easier
        if (isStaff.value) {
            const oneMinuteInMilliseconds = MILLISECONDS_PER_MINUTE
            const twoMinutesInMilliseconds = 2 * oneMinuteInMilliseconds
            const threeMinutesInMilliseconds = 3 * oneMinuteInMilliseconds
            const fourMinutesInMilliseconds = 4 * oneMinuteInMilliseconds
            const fiveMinutesInMilliseconds = 5 * oneMinuteInMilliseconds

            intervalsList = [
                oneMinuteInMilliseconds,
                twoMinutesInMilliseconds,
                threeMinutesInMilliseconds,
                fourMinutesInMilliseconds,
                fiveMinutesInMilliseconds,
                ...intervalsList
            ]
        }

        const intervals = intervalsList.map((duration) => {
            const durationInSeconds = duration / MILLISECONDS_PER_SECOND

            return { duration, text: asStaticTime(durationInSeconds, { timeFormat: 'long', length: 2, smallestUnit: 'minutes' }) }
        })

        return intervals as Interval[]
    })

    const intervalItems = computed((): UISelectItemOption[] => intervals.value.map((interval): UISelectItemOption => ({
        type: 'option',
        label: interval.text,
        value: interval.duration
    })))

    const showRealityCheck = computed(() => {
        if (configurationView.value) return true
        if (currentInterval.value === REALITY_CHECK_UNLIMITED || !currentInterval.value) return false

        return (timer.value * MILLISECONDS_PER_SECOND) >= currentInterval.value
    })

    const syncSelectedIntervalToCurrent = () => {
        if (currentInterval.value === REALITY_CHECK_UNLIMITED) {
            syncSelectedIntervalToDefault()

            return
        }

        selectedInterval.value = currentInterval.value
    }

    const syncSelectedIntervalToDefault = () => {
        selectedInterval.value = Number(defaultInterval)
    }

    const resetDialogState = () => {
        configurationView.value = false
        disabled.value = false
    }

    const isValidInterval = (interval: number | typeof REALITY_CHECK_UNLIMITED | undefined): boolean => {
        if (typeof interval !== 'number' || interval <= 0) return false

        return intervals.value.some(i => i.duration === interval)
    }

    const getInterval = async () => {
        isProcessing.value = true

        try {
            // force a fresh network request to get the latest setting
            await settingQuery.refresh()

            const setting = settingQuery.data.value

            // no row saved server-side - fall back to the frontend default
            if (!setting || !('intervalMinutes' in setting)) {
                savedIntervalFromApi.value = undefined

                syncSelectedIntervalToDefault()

                return
            }

            // 'inf' is an explicit opt-out - keep reality check off
            if (setting.intervalMinutes === REALITY_CHECK_UNLIMITED) {
                savedIntervalFromApi.value = REALITY_CHECK_UNLIMITED

                syncSelectedIntervalToDefault()

                return
            }

            // api gives us minutes, we convert to milliseconds internally
            savedIntervalFromApi.value = Number(setting.intervalMinutes) * MILLISECONDS_PER_MINUTE

            syncSelectedIntervalToCurrent()
        }
        catch (error) {
            // if the site allows disabling, clear the interval
            if (realityCheck.isDisableable) {
                savedIntervalFromApi.value = undefined
                selectedInterval.value = undefined

                throw error
            }

            // otherwise, use the default interval (no custom setting)
            savedIntervalFromApi.value = undefined

            syncSelectedIntervalToDefault()

            throw error
        }
        finally {
            isProcessing.value = false
        }
    }

    /**
     * Save the selected interval to the API
     * Note: Caller is responsible for managing isProcessing state
     */
    const saveInterval = async () => {
        if (!isValidInterval(selectedInterval.value)) {
            throw new Error('Selected interval is required')
        }

        const selectedIntervalMs = selectedInterval.value!

        // Convert the selected interval to minutes for the api
        const intervalMinutes = Math.round(selectedIntervalMs / MILLISECONDS_PER_MINUTE)

        if (intervalMinutes == null || intervalMinutes <= 0) {
            throw new Error('Interval minutes cannot be null, undefined, or zero')
        }

        // the api models intervalMinutes as a string because it can also be 'inf'
        await updateSettingMutation.mutate({ intervalMinutes: String(intervalMinutes) })

        // update state from the mutation result
        // use the mutation data directly or fall back to what we sent
        const result = updateSettingMutation.data.value
        const savedMinutes = (result as any)?.intervalMinutes ?? intervalMinutes
        const interval = Number(savedMinutes) * MILLISECONDS_PER_MINUTE

        savedIntervalFromApi.value = interval
        selectedInterval.value = interval

        return updateSettingMutation.data.value
    }

    /**
     * persist the user's opt-out so reality check stays off across sessions
     * caller is responsible for managing isProcessing state
     */
    const disableInterval = async () => {
        await updateSettingMutation.mutate({ intervalMinutes: REALITY_CHECK_UNLIMITED })

        savedIntervalFromApi.value = REALITY_CHECK_UNLIMITED

        syncSelectedIntervalToDefault()

        return updateSettingMutation.data.value
    }

    const getCurrentLogin = async () => {
        if (!sessionQuery.data.value) {
            await sessionQuery.refresh()
        }

        return sessionQuery.data.value?.currentLogin ?? undefined
    }

    const startPlayClock = async () => {
        // read before the session lookup, so a slow lookup does not shorten the play time
        const startedAt = dayjs().valueOf()
        const login = await getCurrentLogin()

        if (!login || playClock.value.login === login) return

        playClock.value = { login, startedAt }
    }

    const calculateSpentTime = async () => {
        const login = await getCurrentLogin()

        // without a clock for this login, the login time is the best upper bound we have
        const startedAt = (login && playClock.value.login === login ? playClock.value.startedAt : undefined) ?? login

        playStartedAt.value = startedAt ? dayjs(startedAt).tz() : dayjs().tz()

        const difference = dayjs().valueOf() - playStartedAt.value.valueOf()
        const hours = Math.floor(difference / MILLISECONDS_PER_HOUR)
        const minutes = Math.floor((difference % MILLISECONDS_PER_HOUR) / MILLISECONDS_PER_MINUTE)

        timeSpent.value = { hours, minutes }
    }

    /**
     * get how much the user has spent/won in this session
     */
    const fetchSessionData = async () => {
        sessionDetails.value = {}

        try {
            sessionDetails.value = await getRealityCheck() ?? {}
        }
        catch (error) {
            // log error but still show dialog without spend info
            console.error('[RealityCheck] Failed to fetch session data:', error)
        }
    }

    const initializeRealityCheck = async () => {
        await getInterval()
        await getCurrentLogin()
    }

    const trackRealityCheckOpened = () => {
        const category = route.meta.isPlaygame ? 'playgame' : 'site'

        trackEvent({
            event_name: 'reality_check_opened',
            event_type: 'Click',
            meta_data: [{ category }],
        })
    }

    const stopAutoSpinningGames = async () => {
        gamesArray.value.forEach((game) => {
            if (!game.isAutoSpinning) return

            eventTrigger(game.identifier, EVENT_ENABLE_AUTO_PLAY, { enabled: true })
            eventTrigger(game.identifier, EVENT_TOGGLE_AUTO_SPIN, { enabled: false })
        })
    }

    const open = async (options: OpenOptions = {}) => {
        const { configurationView: isConfigView = false } = options

        pauseTimer()

        // pre-check the disable option for users with a persisted opt-out, so
        // continuing from the dialog keeps them opted out instead of silently
        // re-enabling the check with the default interval
        disabled.value = isRealityCheckDisabled.value

        // sync selectedInterval to currentInterval when opening dialog
        // this ensures it shows the correct saved value instead of defaultInterval
        if (!selectedInterval.value || selectedInterval.value !== currentInterval.value) {
            syncSelectedIntervalToCurrent()
        }

        // make sure the selected interval is actually one of the valid options
        if (!isValidInterval(selectedInterval.value)) {
            // if selectedInterval is invalid, prefer currentInterval over defaultInterval
            if (isValidInterval(currentInterval.value)) {
                syncSelectedIntervalToCurrent()
            }
            else {
                syncSelectedIntervalToDefault()
            }
        }

        trackRealityCheckOpened()

        if (isConfigView) {
            configurationView.value = true

            return
        }

        await calculateSpentTime()
        await stopAutoSpinningGames()
        await fetchSessionData()
    }

    const saveRealityCheckSettings = async () => {
        if (isProcessing.value) {
            return { success: false, error: new Error('Already processing') }
        }

        if (!isValidInterval(selectedInterval.value)) {
            return { success: false, error: new Error('Please select a valid interval') }
        }

        try {
            isProcessing.value = true

            await saveInterval()

            return { success: true }
        }
        catch (error) {
            console.error('[RealityCheck] saveRealityCheckSettings: Error', error)

            return { success: false, error }
        }
        finally {
            isProcessing.value = false
        }
    }

    const disableRealityCheckSettings = async () => {
        if (isProcessing.value) {
            return { success: false, error: new Error('Already processing') }
        }

        try {
            isProcessing.value = true

            await disableInterval()

            return { success: true }
        }
        catch (error) {
            return { success: false, error }
        }
        finally {
            isProcessing.value = false
        }
    }

    /**
     * user clicked "Continue Playing" - save their settings and restart the timer
     * this handles all the dialog-specific stuff like closing it and restarting the timer
     */
    const continueRealityCheck = async () => {
        if (isProcessing.value) {
            return
        }

        timer.value = 0

        if (disabled.value && realityCheck.isDisableable) {
            const result = await disableRealityCheckSettings()

            if (!result.success) {
                return
            }
        }
        else {
            const result = await saveRealityCheckSettings()

            if (!result.success) {
                console.error('[RealityCheck] continueRealityCheck: Failed to save settings', result.error)

                return
            }
        }

        resetDialogState()

        if (!isRealityCheckDisabled.value) {
            start()
        }
    }

    /**
     * user clicked "Stop Playing" - close all the games
     */
    const stopGame = async () => {
        if (isProcessing.value) return

        // close the dialog first
        resetDialogState()

        // then close all the games
        await closePlaygame(true)
    }

    const start = async () => {
        if (!gamesArray.value.length) {
            pauseTimer()

            return
        }

        // not awaited: the reminder timer must not wait for the session lookup
        startPlayClock()

        // A timer has started already
        if (timer.value) {
            resumeTimer()

            return
        }

        // if we don't know what interval to use, fetch it from the api
        if (!currentInterval.value) {
            await getInterval()
        }

        // the user opted out ('inf') or no interval is known - never start the timer
        if (isRealityCheckDisabled.value || !currentInterval.value) {
            return
        }

        // resume the interval timer
        resumeTimer()
    }

    /**
     * navigate to the user's account page to see their transaction history
     */
    const goToAccount = () => {
        navigateTo(realityCheck.accountPath, { open: { target: '_blank' } })
    }

    return {
        pauseTimer,
        resumeTimer,
        configurationView,
        sessionDetails,
        playStartedAt,
        timeSpent,
        disabled,
        isProcessing,
        isRealityCheckDisabled,
        selectedInterval,
        showRealityCheck,
        intervalItems,
        hasValidSelectedInterval,
        continueRealityCheck,
        stopGame,
        goToAccount,
        initializeRealityCheck,
        start,
        open,
        saveRealityCheckSettings,
        disableRealityCheckSettings
    }
}
